import { isMacintosh } from 'vscode/vscode/vs/base/common/platform';
import { localize2WithPath } from 'vscode/vscode/vs/nls';
import { IConfigurationService } from 'vscode/vscode/vs/platform/configuration/common/configuration.service';
import { IViewsService } from 'vscode/vscode/vs/workbench/services/views/common/viewsService.service';
import { SearchContext } from 'vscode/vscode/vs/workbench/contrib/search/common/constants';
import { InSearchEditor } from 'vscode/vscode/vs/workbench/contrib/searchEditor/browser/constants';
import { FolderMatch } from 'vscode/vscode/vs/workbench/contrib/search/browser/searchModel';
import { SearchEditorInput } from '../../searchEditor/browser/searchEditorInput.js';
import { IEditorService } from 'vscode/vscode/vs/workbench/services/editor/common/editorService.service';
import { ContextKeyExpr } from 'vscode/vscode/vs/platform/contextkey/common/contextkey';
import { IContextKeyService } from 'vscode/vscode/vs/platform/contextkey/common/contextkey.service';
import { assertIsDefined } from 'vscode/vscode/vs/base/common/types';
import { registerAction2, Action2, MenuId } from 'vscode/vscode/vs/platform/actions/common/actions';
import { ToggleCaseSensitiveKeybinding, ToggleWholeWordKeybinding, ToggleRegexKeybinding, TogglePreserveCaseKeybinding } from 'vscode/vscode/vs/editor/contrib/find/browser/findModel';
import { category, getSearchView, openSearchView } from 'vscode/vscode/vs/workbench/contrib/search/browser/searchActionsBase';
import { CONTEXT_ACCESSIBILITY_MODE_ENABLED } from 'vscode/vscode/vs/platform/accessibility/common/accessibility';
import { getActiveElement } from 'vscode/vscode/vs/base/browser/dom';

const _moduleId = "vs/workbench/contrib/search/browser/searchActionsNav";
registerAction2(class ToggleQueryDetailsAction extends Action2 {
    constructor() {
        super({
            id: "workbench.action.search.toggleQueryDetails" ,
            title: ( localize2WithPath(_moduleId, 0, "Toggle Query Details")),
            category,
            keybinding: {
                weight: 200 ,
                when: ( (ContextKeyExpr.or(SearchContext.SearchViewFocusedKey, InSearchEditor))),
                primary: 2048  | 1024  | 40 ,
            },
        });
    }
    run(accessor, ...args) {
        const contextService = accessor.get(IContextKeyService).getContext(getActiveElement());
        if (contextService.getValue(InSearchEditor.serialize())) {
            accessor.get(IEditorService).activeEditorPane.toggleQueryDetails(args[0]?.show);
        }
        else if (contextService.getValue(SearchContext.SearchViewFocusedKey.serialize())) {
            const searchView = getSearchView(accessor.get(IViewsService));
            assertIsDefined(searchView).toggleQueryDetails(undefined, args[0]?.show);
        }
    }
});
registerAction2(class CloseReplaceAction extends Action2 {
    constructor() {
        super({
            id: "closeReplaceInFilesWidget" ,
            title: ( localize2WithPath(_moduleId, 1, "Close Replace Widget")),
            category,
            keybinding: {
                weight: 200 ,
                when: ( (ContextKeyExpr.and(
                    SearchContext.SearchViewVisibleKey,
                    SearchContext.ReplaceInputBoxFocusedKey
                ))),
                primary: 9 ,
            },
        });
    }
    run(accessor) {
        const searchView = getSearchView(accessor.get(IViewsService));
        if (searchView) {
            searchView.searchAndReplaceWidget.toggleReplace(false);
            searchView.searchAndReplaceWidget.focus();
        }
        return Promise.resolve(null);
    }
});
registerAction2(class ToggleCaseSensitiveCommandAction extends Action2 {
    constructor() {
        super({
            id: "toggleSearchCaseSensitive" ,
            title: ( localize2WithPath(_moduleId, 2, "Toggle Case Sensitive")),
            category,
            keybinding: Object.assign({
                weight: 200 ,
                when: isMacintosh ? ( (ContextKeyExpr.and(
                    SearchContext.SearchViewFocusedKey,
                     (SearchContext.FileMatchOrFolderMatchFocusKey.toNegated())
                ))) : SearchContext.SearchViewFocusedKey,
            }, ToggleCaseSensitiveKeybinding)
        });
    }
    async run(accessor) {
        toggleCaseSensitiveCommand(accessor);
    }
});
registerAction2(class ToggleWholeWordCommandAction extends Action2 {
    constructor() {
        super({
            id: "toggleSearchWholeWord" ,
            title: ( localize2WithPath(_moduleId, 3, "Toggle Whole Word")),
            keybinding: Object.assign({
                weight: 200 ,
                when: SearchContext.SearchViewFocusedKey,
            }, ToggleWholeWordKeybinding),
            category,
        });
    }
    async run(accessor) {
        return toggleWholeWordCommand(accessor);
    }
});
registerAction2(class ToggleRegexCommandAction extends Action2 {
    constructor() {
        super({
            id: "toggleSearchRegex" ,
            title: ( localize2WithPath(_moduleId, 4, "Toggle Regex")),
            keybinding: Object.assign({
                weight: 200 ,
                when: SearchContext.SearchViewFocusedKey,
            }, ToggleRegexKeybinding),
            category,
        });
    }
    async run(accessor) {
        return toggleRegexCommand(accessor);
    }
});
registerAction2(class TogglePreserveCaseAction extends Action2 {
    constructor() {
        super({
            id: "toggleSearchPreserveCase" ,
            title: ( localize2WithPath(_moduleId, 5, "Toggle Preserve Case")),
            keybinding: Object.assign({
                weight: 200 ,
                when: SearchContext.SearchViewFocusedKey,
            }, TogglePreserveCaseKeybinding),
            category,
        });
    }
    async run(accessor) {
        return togglePreserveCaseCommand(accessor);
    }
});
registerAction2(class OpenMatchAction extends Action2 {
    constructor() {
        super({
            id: "search.action.openResult" ,
            title: ( localize2WithPath(_moduleId, 6, "Open Match")),
            category,
            keybinding: {
                weight: 200 ,
                when: ( (ContextKeyExpr.and(SearchContext.SearchViewVisibleKey, SearchContext.FileMatchOrMatchFocusKey))),
                primary: 3 ,
                mac: {
                    primary: 3 ,
                    secondary: [2048  | 18 ]
                },
            },
        });
    }
    run(accessor) {
        const searchView = getSearchView(accessor.get(IViewsService));
        if (searchView) {
            const tree = searchView.getControl();
            const viewer = searchView.getControl();
            const focus = tree.getFocus()[0];
            if (focus instanceof FolderMatch) {
                viewer.toggleCollapsed(focus);
            }
            else {
                searchView.open(tree.getFocus()[0], false, false, true);
            }
        }
    }
});
registerAction2(class OpenMatchToSideAction extends Action2 {
    constructor() {
        super({
            id: "search.action.openResultToSide" ,
            title: ( localize2WithPath(_moduleId, 7, "Open Match To Side")),
            category,
            keybinding: {
                weight: 200 ,
                when: ( (ContextKeyExpr.and(SearchContext.SearchViewVisibleKey, SearchContext.FileMatchOrMatchFocusKey))),
                primary: 2048  | 3 ,
                mac: {
                    primary: 256  | 3
                },
            },
        });
    }
    run(accessor) {
        const searchView = getSearchView(accessor.get(IViewsService));
        if (searchView) {
            const tree = searchView.getControl();
            searchView.open(tree.getFocus()[0], false, true, true);
        }
    }
});
registerAction2(class AddCursorsAtSearchResultsAction extends Action2 {
    constructor() {
        super({
            id: "addCursorsAtSearchResults" ,
            title: ( localize2WithPath(_moduleId, 8, "Add Cursors at Search Results")),
            keybinding: {
                weight: 200 ,
                when: ( (ContextKeyExpr.and(SearchContext.SearchViewVisibleKey, SearchContext.FileMatchOrMatchFocusKey))),
                primary: 2048  | 1024  | 42 ,
            },
            category,
        });
    }
    async run(accessor) {
        const searchView = getSearchView(accessor.get(IViewsService));
        if (searchView) {
            const tree = searchView.getControl();
            searchView.openEditorWithMultiCursor(tree.getFocus()[0]);
        }
    }
});
registerAction2(class FocusNextInputAction extends Action2 {
    constructor() {
        super({
            id: "search.focus.nextInputBox" ,
            title: ( localize2WithPath(_moduleId, 9, "Focus Next Input")),
            category,
            keybinding: {
                weight: 200 ,
                when: ( (ContextKeyExpr.or(
                     (ContextKeyExpr.and(InSearchEditor, SearchContext.InputBoxFocusedKey)),
                     (ContextKeyExpr.and(SearchContext.SearchViewVisibleKey, SearchContext.InputBoxFocusedKey))
                ))),
                primary: 2048  | 18 ,
            },
        });
    }
    async run(accessor) {
        const editorService = accessor.get(IEditorService);
        const input = editorService.activeEditor;
        if (input instanceof SearchEditorInput) {
            editorService.activeEditorPane.focusNextInput();
        }
        const searchView = getSearchView(accessor.get(IViewsService));
        searchView?.focusNextInputBox();
    }
});
registerAction2(class FocusPreviousInputAction extends Action2 {
    constructor() {
        super({
            id: "search.focus.previousInputBox" ,
            title: ( localize2WithPath(_moduleId, 10, "Focus Previous Input")),
            category,
            keybinding: {
                weight: 200 ,
                when: ( (ContextKeyExpr.or(
                     (ContextKeyExpr.and(InSearchEditor, SearchContext.InputBoxFocusedKey)),
                     (ContextKeyExpr.and(
                        SearchContext.SearchViewVisibleKey,
                        SearchContext.InputBoxFocusedKey,
                         (SearchContext.SearchInputBoxFocusedKey.toNegated())
                    ))
                ))),
                primary: 2048  | 16 ,
            },
        });
    }
    async run(accessor) {
        const editorService = accessor.get(IEditorService);
        const input = editorService.activeEditor;
        if (input instanceof SearchEditorInput) {
            editorService.activeEditorPane.focusPrevInput();
        }
        const searchView = getSearchView(accessor.get(IViewsService));
        searchView?.focusPreviousInputBox();
    }
});
registerAction2(class FocusSearchFromResultsAction extends Action2 {
    constructor() {
        super({
            id: "search.action.focusSearchFromResults" ,
            title: ( localize2WithPath(_moduleId, 11, "Focus Search From Results")),
            category,
            keybinding: {
                weight: 200 ,
                when: ( (ContextKeyExpr.and(
                    SearchContext.SearchViewVisibleKey,
                     (ContextKeyExpr.or(SearchContext.FirstMatchFocusKey, CONTEXT_ACCESSIBILITY_MODE_ENABLED))
                ))),
                primary: 2048  | 16 ,
            },
        });
    }
    run(accessor) {
        const searchView = getSearchView(accessor.get(IViewsService));
        searchView?.focusPreviousInputBox();
    }
});
registerAction2(class ToggleSearchOnTypeAction extends Action2 {
    static { this.searchOnTypeKey = 'search.searchOnType'; }
    constructor() {
        super({
            id: "workbench.action.toggleSearchOnType" ,
            title: ( localize2WithPath(_moduleId, 12, "Toggle Search on Type")),
            category,
        });
    }
    async run(accessor) {
        const configurationService = accessor.get(IConfigurationService);
        const searchOnType = configurationService.getValue(ToggleSearchOnTypeAction.searchOnTypeKey);
        return configurationService.updateValue(ToggleSearchOnTypeAction.searchOnTypeKey, !searchOnType);
    }
});
registerAction2(class FocusSearchListCommandAction extends Action2 {
    constructor() {
        super({
            id: "search.action.focusSearchList" ,
            title: ( localize2WithPath(_moduleId, 13, "Focus List")),
            category,
            f1: true
        });
    }
    async run(accessor) {
        focusSearchListCommand(accessor);
    }
});
registerAction2(class FocusNextSearchResultAction extends Action2 {
    constructor() {
        super({
            id: "search.action.focusNextSearchResult" ,
            title: ( localize2WithPath(_moduleId, 14, "Focus Next Search Result")),
            keybinding: [{
                    primary: 62 ,
                    weight: 200 ,
                }],
            category,
            f1: true,
            precondition: ( (ContextKeyExpr.or(SearchContext.HasSearchResults, InSearchEditor))),
        });
    }
    async run(accessor) {
        return await focusNextSearchResult(accessor);
    }
});
registerAction2(class FocusPreviousSearchResultAction extends Action2 {
    constructor() {
        super({
            id: "search.action.focusPreviousSearchResult" ,
            title: ( localize2WithPath(_moduleId, 15, "Focus Previous Search Result")),
            keybinding: [{
                    primary: 1024  | 62 ,
                    weight: 200 ,
                }],
            category,
            f1: true,
            precondition: ( (ContextKeyExpr.or(SearchContext.HasSearchResults, InSearchEditor))),
        });
    }
    async run(accessor) {
        return await focusPreviousSearchResult(accessor);
    }
});
registerAction2(class ReplaceInFilesAction extends Action2 {
    constructor() {
        super({
            id: "workbench.action.replaceInFiles" ,
            title: ( localize2WithPath(_moduleId, 16, "Replace in Files")),
            keybinding: [{
                    primary: 2048  | 1024  | 38 ,
                    weight: 200 ,
                }],
            category,
            f1: true,
            menu: [{
                    id: MenuId.MenubarEditMenu,
                    group: '4_find_global',
                    order: 2
                }],
        });
    }
    async run(accessor) {
        return await findOrReplaceInFiles(accessor, true);
    }
});
function toggleCaseSensitiveCommand(accessor) {
    const searchView = getSearchView(accessor.get(IViewsService));
    searchView?.toggleCaseSensitive();
}
function toggleWholeWordCommand(accessor) {
    const searchView = getSearchView(accessor.get(IViewsService));
    searchView?.toggleWholeWords();
}
function toggleRegexCommand(accessor) {
    const searchView = getSearchView(accessor.get(IViewsService));
    searchView?.toggleRegex();
}
function togglePreserveCaseCommand(accessor) {
    const searchView = getSearchView(accessor.get(IViewsService));
    searchView?.togglePreserveCase();
}
const focusSearchListCommand = accessor => {
    const viewsService = accessor.get(IViewsService);
    openSearchView(viewsService).then(searchView => {
        searchView?.moveFocusToResults();
    });
};
async function focusNextSearchResult(accessor) {
    const editorService = accessor.get(IEditorService);
    const input = editorService.activeEditor;
    if (input instanceof SearchEditorInput) {
        return editorService.activeEditorPane.focusNextResult();
    }
    return openSearchView(accessor.get(IViewsService)).then(searchView => {
        searchView?.selectNextMatch();
    });
}
async function focusPreviousSearchResult(accessor) {
    const editorService = accessor.get(IEditorService);
    const input = editorService.activeEditor;
    if (input instanceof SearchEditorInput) {
        return editorService.activeEditorPane.focusPreviousResult();
    }
    return openSearchView(accessor.get(IViewsService)).then(searchView => {
        searchView?.selectPreviousMatch();
    });
}
async function findOrReplaceInFiles(accessor, expandSearchReplaceWidget) {
    return openSearchView(accessor.get(IViewsService), false).then(openedView => {
        if (openedView) {
            const searchAndReplaceWidget = openedView.searchAndReplaceWidget;
            searchAndReplaceWidget.toggleReplace(expandSearchReplaceWidget);
            const updatedText = openedView.updateTextFromFindWidgetOrSelection({ allowUnselectedWord: !expandSearchReplaceWidget });
            openedView.searchAndReplaceWidget.focus(undefined, updatedText, updatedText);
        }
    });
}
